import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import get from 'lodash/get';

import { PreAuth, PreAuthsResponse } from '../types';

interface UseLocationPreauthsReturn {
  preauths: PreAuth[];
  oldestPreauth: PreAuth | null;
  otherPreauths: PreAuth[];
  isLoading: boolean;
  isError: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const useLocationPreauths = (expiringBefore?: Date): UseLocationPreauthsReturn => {
  const {
    data: preauths = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<PreAuth[], Error>({
    queryKey: ['location-preauths', expiringBefore?.toISOString()],
    queryFn: async () => {
      const allPreauths: PreAuth[] = [];
      const startingAfter: string | null = null;

      // Fetching first page only to avoid multiple calls in a high traffic page
      // If needed, a separate API will be created to fetch the count in a single call
      const params = { starting_after: startingAfter } as any;
      const response = await axios.get<PreAuthsResponse>('/admin/payments/pre-authorizations', {
        params,
      });

      const pagePreauths = response.data.data || [];
      allPreauths.push(...pagePreauths);

      return allPreauths;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  let filteredPreauths = preauths;

  if (expiringBefore) {
    filteredPreauths = preauths.filter((preauth) => {
      const preauthExpiry = new Date(preauth.captureDeadline);
      return preauthExpiry <= expiringBefore;
    });
  }

  // Determine which preauth to capture
  let oldestPreauth: PreAuth | null = null;
  let otherPreauths: PreAuth[] = [];

  if (filteredPreauths.length > 0) {
    oldestPreauth = filteredPreauths[0];
    otherPreauths = filteredPreauths.slice(1);
  }

  return {
    preauths: filteredPreauths,
    oldestPreauth,
    otherPreauths,
    isLoading,
    isError,
    error: error ? get(error, 'message', 'Failed to fetch Preauthorizations') : null,
    refetch: async () => {
      await refetch();
    },
  };
};

export default useLocationPreauths;
